import React, { useState } from 'react';
import { AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { AlertTriangle, MapPin, Clock, Info, Activity, Maximize, Minimize, ArrowRight, Shield } from 'lucide-react';
import { SafetyAlert, SafetyStats, RiskZone, AlertHistoryItem } from './types';

interface RiskAnalysisTabProps {
  riskZones: RiskZone[];
  alerts: SafetyAlert[];
  stats: SafetyStats | null;
  alertHistory: AlertHistoryItem[];
}

const RiskAnalysisTab: React.FC<RiskAnalysisTabProps> = ({ 
  riskZones, 
  alerts, 
  stats, 
  alertHistory 
}) => {
  // State for selected risk zone and time period
  const [selectedRiskZone, setSelectedRiskZone] = useState<RiskZone | null>(null);
  const [timePeriod, setTimePeriod] = useState<'day' | 'week' | 'month'>('week');
  const [showFullFloorplan, setShowFullFloorplan] = useState(false);
  
  // Calculate high-risk time periods based on alert history
  const getTimeBasedRiskData = () => {
    const timeBlocks = [
      { time: '12am-3am', count: 0, risk: 0 },
      { time: '3am-6am', count: 0, risk: 0 },
      { time: '6am-9am', count: 0, risk: 0 },
      { time: '9am-12pm', count: 0, risk: 0 },
      { time: '12pm-3pm', count: 0, risk: 0 },
      { time: '3pm-6pm', count: 0, risk: 0 },
      { time: '6pm-9pm', count: 0, risk: 0 },
      { time: '9pm-12am', count: 0, risk: 0 }
    ];
    
    // Add random but realistic data for demonstration
    return timeBlocks.map(block => {
      // Morning and evening highest risk for elderly
      let riskFactor = 1;
      if (block.time === '6am-9am' || block.time === '9pm-12am') {
        riskFactor = 2.5;
      } else if (block.time === '9am-12pm' || block.time === '6pm-9pm') {
        riskFactor = 1.8;
      }
      
      const count = Math.floor(Math.random() * 10 * riskFactor);
      return {
        ...block,
        count,
        risk: Math.min(count * 10, 100) // Calculate risk score (0-100)
      };
    });
  };
  
  // Format risk zones for rendering on floorplan
  const formatRiskZonesForDisplay = () => {
    // Group risk zones by room
    const roomGroups: Record<string, RiskZone[]> = {};
    
    riskZones.forEach(zone => {
      const room = zone.location.room;
      if (!roomGroups[room]) {
        roomGroups[room] = [];
      }
      roomGroups[room].push(zone);
    });
    
    return roomGroups;
  };
  
  // Get trend data for alerts over time
  const getAlertTrendData = () => {
    let data = [];
    
    if (timePeriod === 'day') {
      // Last 24 hours in 3-hour increments
      data = [
        { time: '12am-3am', alerts: Math.floor(Math.random() * 3) },
        { time: '3am-6am', alerts: Math.floor(Math.random() * 2) },
        { time: '6am-9am', alerts: Math.floor(Math.random() * 5) + 2 },
        { time: '9am-12pm', alerts: Math.floor(Math.random() * 4) + 1 },
        { time: '12pm-3pm', alerts: Math.floor(Math.random() * 3) },
        { time: '3pm-6pm', alerts: Math.floor(Math.random() * 4) + 1 },
        { time: '6pm-9pm', alerts: Math.floor(Math.random() * 5) + 2 },
        { time: '9pm-12am', alerts: Math.floor(Math.random() * 4) + 1 }
      ];
    } else if (timePeriod === 'week') {
      // Last 7 days
      data = [
        { time: 'Monday', alerts: Math.floor(Math.random() * 10) + 3 },
        { time: 'Tuesday', alerts: Math.floor(Math.random() * 10) + 5 },
        { time: 'Wednesday', alerts: Math.floor(Math.random() * 10) + 2 },
        { time: 'Thursday', alerts: Math.floor(Math.random() * 10) + 4 },
        { time: 'Friday', alerts: Math.floor(Math.random() * 10) + 6 },
        { time: 'Saturday', alerts: Math.floor(Math.random() * 10) + 3 },
        { time: 'Sunday', alerts: Math.floor(Math.random() * 10) + 1 }
      ];
    } else {
      // Last 30 days in weekly increments
      data = [
        { time: 'Week 1', alerts: Math.floor(Math.random() * 30) + 15 },
        { time: 'Week 2', alerts: Math.floor(Math.random() * 30) + 20 },
        { time: 'Week 3', alerts: Math.floor(Math.random() * 30) + 10 },
        { time: 'Week 4', alerts: Math.floor(Math.random() * 30) + 18 }
      ];
    }
    
    return data;
  };
  
  // Get risk level color
  const getRiskLevelColor = (riskLevel: 'HIGH' | 'MEDIUM' | 'LOW') => {
    switch (riskLevel) {
      case 'HIGH':
        return 'text-red-500';
      case 'MEDIUM':
        return 'text-yellow-500';
      case 'LOW':
        return 'text-green-500';
      default:
        return 'text-gray-500';
    }
  };
  
  const getRiskBgColor = (score: number) => {
    if (score >= 70) return 'bg-red-500';
    if (score >= 50) return 'bg-yellow-500';
    return 'bg-green-500';
  };
  
  const getRiskTextColor = (score: number) => {
    if (score >= 70) return 'text-red-500';
    if (score >= 50) return 'text-yellow-500';
    return 'text-green-500';
  };
  
  // Utility to get a formatted date
  const formatDate = (date: Date): string => {
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    }).format(date);
  };
  
  // Rooms and risk zones
  const roomGroups = formatRiskZonesForDisplay();
  const timeBasedRiskData = getTimeBasedRiskData();
  const alertTrendData = getAlertTrendData();
  
  return (
    <div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        {/* Left Column: Risk Zone Map */}
        <div className="lg:col-span-2">
          <div className="bg-white rounded-lg shadow">
            <div className="p-4 border-b border-gray-200 flex justify-between items-center">
              <h3 className="text-lg font-semibold">Risk Zones by Location</h3>
              <button
                onClick={() => setShowFullFloorplan(!showFullFloorplan)}
                className="flex items-center text-gray-600 hover:text-blue-600"
              >
                {showFullFloorplan ? (
                  <>
                    <Minimize size={16} className="mr-1" />
                    <span className="text-sm">Minimize</span>
                  </>
                ) : (
                  <>
                    <Maximize size={16} className="mr-1" />
                    <span className="text-sm">Expand</span>
                  </>
                )}
              </button>
            </div>
            
            {/* Floor Plan Visualization */}
            <div className={`p-4 ${showFullFloorplan ? 'h-[600px]' : 'h-[400px]'} relative bg-gray-50 overflow-hidden rounded-b-lg`}>
              {/* Sample floorplan map - in a real app, this would be an actual floor plan */}
              <div className="absolute inset-0 p-4">
                {/* Grid layout representing rooms */}
                <div className="grid grid-cols-3 grid-rows-2 gap-4 h-full">
                  {Object.entries(roomGroups).map(([roomName, zones], roomIndex) => {
                    // Find highest risk zone in this room
                    const highestRiskZone = zones.reduce((prev, current) => 
                      (current.riskScore > prev.riskScore) ? current : prev, zones[0]);
                    
                    const roomRiskClass = 
                      highestRiskZone.riskLevel === 'HIGH' ? 'border-red-300 bg-red-50' :
                      highestRiskZone.riskLevel === 'MEDIUM' ? 'border-yellow-300 bg-yellow-50' :
                      'border-green-300 bg-green-50';
                    
                    return (
                      <div 
                        key={roomName} 
                        className={`relative border-2 ${roomRiskClass} p-2 rounded-lg cursor-pointer hover:opacity-90 transition-opacity`}
                        onClick={() => setSelectedRiskZone(highestRiskZone)}
                      >
                        <div className="absolute top-2 left-2">
                          <h4 className="text-sm font-medium text-gray-700">{roomName}</h4>
                        </div>
                        
                        {/* Risk hotspots */}
                        {zones.map(zone => (
                          <div
                            key={zone.id}
                            className={`absolute w-6 h-6 rounded-full opacity-70 ${
                              zone.riskLevel === 'HIGH' ? 'bg-red-500' :
                              zone.riskLevel === 'MEDIUM' ? 'bg-yellow-500' :
                              'bg-green-500'
                            } flex items-center justify-center`}
                            style={{
                              left: `${zone.location.coordinates.x}%`,
                              top: `${zone.location.coordinates.y}%`,
                              transform: 'translate(-50%, -50%)'
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedRiskZone(zone);
                            }}
                          >
                            <AlertTriangle 
                              className="text-white" 
                              size={14} 
                            />
                          </div>
                        ))}
                        
                        {/* Risk score */}
                        <div className="absolute bottom-2 right-2 px-2 py-1 rounded-full bg-white text-xs font-medium">
                          Risk: <span className={getRiskTextColor(highestRiskZone.riskScore)}>{highestRiskZone.riskScore}%</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Right Column: Selected Risk Info or Top Risk Areas */}
        <div className="lg:col-span-1">
          {selectedRiskZone ? (
            <div className="bg-white rounded-lg shadow p-4">
              <div className="flex justify-between items-start mb-4">
                <h3 className="text-lg font-semibold">Risk Zone Details</h3>
                <button
                  onClick={() => setSelectedRiskZone(null)}
                  className="text-gray-400 hover:text-gray-500"
                >
                  <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                      clipRule="evenodd"
                    />
                  </svg>
                </button>
              </div>
              
              <div className="mb-4 flex items-center">
                <MapPin className="text-gray-500 mr-2" size={18} />
                <span className="text-lg font-medium">
                  {selectedRiskZone.location.room} ({selectedRiskZone.location.zone})
                </span>
              </div>
              
              <div className="mb-4">
                <div className="flex justify-between items-center mb-1">
                  <span className="text-sm font-medium text-gray-700">Risk Score</span>
                  <span className={`font-bold ${getRiskTextColor(selectedRiskZone.riskScore)}`}>
                    {selectedRiskZone.riskScore}%
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2.5">
                  <div
                    className={`h-2.5 rounded-full ${getRiskBgColor(selectedRiskZone.riskScore)}`}
                    style={{ width: `${selectedRiskZone.riskScore}%` }}
                  ></div>
                </div>
              </div>
              
              <div className="mb-4">
                <h4 className="text-sm font-medium text-gray-700 mb-2">Risk Factors</h4>
                <ul className="list-disc pl-5 space-y-1 text-sm text-gray-600">
                  {selectedRiskZone.riskFactors.map((factor, index) => (
                    <li key={index}>{factor}</li>
                  ))}
                </ul>
              </div>
              
              <div className="mb-4">
                <div className="flex justify-between items-center mb-1">
                  <h4 className="text-sm font-medium text-gray-700">Alert Frequency</h4>
                  <span className="text-sm text-gray-500">{selectedRiskZone.alertFrequency} per month</span>
                </div>
              </div>
              
              <div className="mb-4">
                <h4 className="text-sm font-medium text-gray-700 mb-1">Trend</h4>
                <div className={`flex items-center ${
                  selectedRiskZone.trend === 'INCREASING' ? 'text-red-500' :
                  selectedRiskZone.trend === 'DECREASING' ? 'text-green-500' :
                  'text-yellow-500'
                }`}>
                  <Activity size={16} className="mr-1" />
                  <span className="text-sm font-medium">
                    {selectedRiskZone.trend === 'INCREASING' ? 'Increasing Risk' :
                     selectedRiskZone.trend === 'DECREASING' ? 'Decreasing Risk' :
                     'Stable Risk'}
                  </span>
                </div>
              </div>
              
              <div className="mb-4">
                <h4 className="text-sm font-medium text-gray-700 mb-1">Last Assessment</h4>
                <span className="text-sm text-gray-600">
                  {formatDate(selectedRiskZone.lastAssessment)}
                </span>
              </div>
              
              <button
                className="w-full flex items-center justify-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm font-medium"
              >
                <Shield className="h-4 w-4 mr-2" />
                View Safety Recommendations
              </button>
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow p-4">
              <h3 className="text-lg font-semibold mb-4">Top Risk Areas</h3>
              
              <div className="space-y-4">
                {riskZones
                  .sort((a, b) => b.riskScore - a.riskScore)
                  .slice(0, 5)
                  .map(zone => (
                    <div 
                      key={zone.id} 
                      className="p-3 bg-gray-50 rounded-lg border border-gray-200 cursor-pointer hover:bg-gray-100"
                      onClick={() => setSelectedRiskZone(zone)}
                    >
                      <div className="flex justify-between items-center mb-1">
                        <h4 className="font-medium">{zone.location.room}</h4>
                        <span className={`text-sm font-medium ${getRiskTextColor(zone.riskScore)}`}>
                          {zone.riskScore}%
                        </span>
                      </div>
                      
                      <div className="text-sm text-gray-600 mb-2 truncate">
                        {zone.riskFactors[0]}
                        {zone.riskFactors.length > 1 && '...'}
                      </div>
                      
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                          className={`h-2 rounded-full ${getRiskBgColor(zone.riskScore)}`}
                          style={{ width: `${zone.riskScore}%` }}
                        ></div>
                      </div>
                      
                      <div className="flex justify-between items-center mt-2 text-xs text-gray-500">
                        <span>{zone.alertFrequency} alerts/month</span>
                        <span className={`${
                          zone.trend === 'INCREASING' ? 'text-red-500' :
                          zone.trend === 'DECREASING' ? 'text-green-500' :
                          'text-yellow-500'
                        }`}>
                          {zone.trend === 'INCREASING' ? '↑' : 
                          zone.trend === 'DECREASING' ? '↓' : '→'} 
                          {zone.trend}
                        </span>
                      </div>
                    </div>
                  ))}
              </div>
              
              <div className="mt-4 text-sm text-gray-600 bg-blue-50 p-3 rounded-lg border border-blue-100">
                <div className="flex items-start">
                  <Info className="text-blue-500 mr-2 flex-shrink-0 mt-0.5" size={16} />
                  <span>Click on any risk zone on the floor plan to view detailed information and recommendations.</span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      
      {/* Time-based Risk Analysis */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Time of Day Risk */}
        <div className="bg-white rounded-lg shadow p-4">
          <h3 className="text-lg font-semibold mb-4">Risk by Time of Day</h3>
          
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={timeBasedRiskData}
                margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="time" />
                <YAxis yAxisId="left" orientation="left" stroke="#8884d8" />
                <YAxis yAxisId="right" orientation="right" stroke="#82ca9d" domain={[0, 100]} />
                <Tooltip />
                <Legend />
                <Bar yAxisId="left" dataKey="count" name="Alert Count" fill="#8884d8" />
                <Bar yAxisId="right" dataKey="risk" name="Risk Score" fill="#82ca9d" />
              </BarChart>
            </ResponsiveContainer>
          </div>
          
          <div className="mt-4 text-sm text-gray-600">
            <div className="flex items-start">
              <Info className="text-blue-500 mr-2 flex-shrink-0 mt-0.5" size={16} />
              <span>
                Highest risk periods are typically early morning (6am-9am) and late evening (9pm-12am), 
                when lighting conditions change and bathroom visits are more frequent.
              </span>
            </div>
          </div>
        </div>
        
        {/* Alert Trend Analysis */}
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold">Alert Trends</h3>
            
            <div className="flex space-x-2">
              <button
                className={`px-3 py-1 text-xs rounded-md ${
                  timePeriod === 'day' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-700'
                }`}
                onClick={() => setTimePeriod('day')}
              >
                24 Hours
              </button>
              <button
                className={`px-3 py-1 text-xs rounded-md ${
                  timePeriod === 'week' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-700'
                }`}
                onClick={() => setTimePeriod('week')}
              >
                Week
              </button>
              <button
                className={`px-3 py-1 text-xs rounded-md ${
                  timePeriod === 'month' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-700'
                }`}
                onClick={() => setTimePeriod('month')}
              >
                Month
              </button>
            </div>
          </div>
          
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart
                data={alertTrendData}
                margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="time" />
                <YAxis />
                <Tooltip />
                <Area 
                  type="monotone" 
                  dataKey="alerts" 
                  name="Alerts" 
                  stroke="#8884d8" 
                  fill="#8884d8" 
                  fillOpacity={0.3} 
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
          
          <div className="mt-4 text-sm text-gray-600">
            <div className="flex items-start">
              <Clock className="text-blue-500 mr-2 flex-shrink-0 mt-0.5" size={16} />
              <span>
                {timePeriod === 'day' 
                  ? 'Alert patterns throughout the day show increased activity during morning and evening hours.'
                  : timePeriod === 'week'
                    ? 'Weekly pattern shows higher alert frequency mid-week.'
                    : 'Monthly trend shows a slightly decreasing pattern of alerts.'
                }
              </span>
            </div>
          </div>
        </div>
      </div>
      
      {/* Risk Insights */}
      <div className="bg-white rounded-lg shadow p-4">
        <h3 className="text-lg font-semibold mb-4">Risk Insights</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <div className="flex items-start space-x-3">
              <AlertTriangle className="text-red-500 flex-shrink-0 mt-1" size={18} />
              <div>
                <h4 className="font-medium">Bathroom Safety Concerns</h4>
                <p className="text-sm text-gray-600">
                  The bathroom has the highest risk score (78%) due to slippery surfaces and poor lighting. 
                  Installing grab bars and improving lighting could reduce this risk significantly.
                </p>
              </div>
            </div>
            
            <div className="flex items-start space-x-3">
              <Clock className="text-yellow-500 flex-shrink-0 mt-1" size={18} />
              <div>
                <h4 className="font-medium">High-Risk Time Periods</h4>
                <p className="text-sm text-gray-600">
                  Early morning (6am-9am) shows elevated risk levels, coinciding with bathroom visits and 
                  morning routine activities. Consider motion-activated night lights and clear pathways.
                </p>
              </div>
            </div>
          </div>
          
          <div className="space-y-4">
            <div className="flex items-start space-x-3">
              <Activity className="text-green-500 flex-shrink-0 mt-1" size={18} />
              <div>
                <h4 className="font-medium">Positive Trend in Living Room</h4>
                <p className="text-sm text-gray-600">
                  Living room risk score has decreased by 15% since implementing furniture rearrangement 
                  and removing trip hazards. Continue monitoring this area.
                </p>
              </div>
            </div>
            
            <div className="flex items-start space-x-3">
              <Shield className="text-blue-500 flex-shrink-0 mt-1" size={18} />
              <div>
                <h4 className="font-medium">Preventive Measures Working</h4>
                <p className="text-sm text-gray-600">
                  Previously implemented safety measures have reduced overall risk level from HIGH to MEDIUM, 
                  with alert frequency decreasing by 22% in the last month.
                </p>
                <button className="mt-2 flex items-center text-blue-600 text-sm">
                  View all safety recommendations <ArrowRight size={14} className="ml-1" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RiskAnalysisTab; 